import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}
function FAQItem({
  question,
  answer
}: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden shadow-sm" data-id="element-216">
      <button className="flex justify-between items-center w-full text-left p-5 bg-white hover:bg-gray-50 transition-colors focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-217">
        <h3 className="text-lg font-medium text-gray-800" data-id="element-218">{question}</h3>
        <span className="text-teal-600" data-id="element-219">
          {isOpen ? <ChevronUpIcon size={20} data-id="element-220" /> : <ChevronDownIcon size={20} data-id="element-221" />}
        </span>
      </button>
      {isOpen && <div className="p-5 bg-white border-t border-gray-200" data-id="element-222">
          <div className="text-gray-700" data-id="element-223">{answer}</div>
        </div>}
    </div>;
}
export function FAQ() {
  return <div className="w-full my-10" data-id="element-224">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-225">
        Still Have Questions? Let's Clear Them Up.
      </h2>
      <p className="text-gray-700 mb-8 text-center" data-id="element-226">
        I know how big this decision can feel — especially when you've already
        tried so many things.
        <br data-id="element-227" />
        So let me walk you through some common questions that might be on your
        mind:
      </p>
      <div className="space-y-4" data-id="element-228">
        <FAQItem question="What exactly is the Neurotoned Method?" answer={<p className="mb-4" data-id="element-230">
              The Neurotoned Method is a simple, step-by-step program that helps
              you calm your nervous system using gentle daily body-based tools.
              <br data-id="element-231" />
              <br data-id="element-232" />
              It focuses on strengthening your vagus nerve — the part of your
              body that controls how safe and calm you feel.
              <br data-id="element-233" />
              <br data-id="element-234" />
              You'll get guided videos, calming audio tools, and short, easy
              exercises that help you stop spirals and feel more grounded each
              day.
            </p>} data-id="element-229" />
        <FAQItem question="Who is this for?" answer={<>
              <p className="mb-4" data-id="element-236">This is for you if:</p>
              <ul className="list-disc pl-6 mb-4" data-id="element-237">
                <li className="mb-2" data-id="element-238">
                  You struggle with health anxiety, spiraling thoughts, or panic
                  symptoms
                </li>
                <li className="mb-2" data-id="element-239">
                  You're tired of feeling like your body is always "on edge"
                </li>
                <li className="mb-2" data-id="element-240">
                  You've tried therapy, supplements, or mindset work — but still
                  feel stuck
                </li>
                <li className="mb-2" data-id="element-241">
                  You want a gentle, non-triggering way to feel calm again
                </li>
              </ul>
              <p data-id="element-242">
                You don't need to be "good at yoga" or "spiritual" or have any
                special skills. Just a willingness to try.
              </p>
            </>} data-id="element-235" />
        <FAQItem question="Do I need a doctor's approval to do this?" answer={<p className="mb-4" data-id="element-244">
              No. The Neurotoned Method is 100% natural and non-medical.
              <br data-id="element-245" />
              <br data-id="element-246" />
              It's designed to be safe, supportive, and gentle — even for people
              with trauma or chronic stress.
              <br data-id="element-247" />
              <br data-id="element-248" />
              If you're under care for something specific, feel free to talk
              with your provider. But most people use this alongside their
              current care with no issues at all.
            </p>} data-id="element-243" />
        <FAQItem question="How long does it take each day?" answer={<p className="mb-4" data-id="element-250">
              Just 7 minutes a day.
              <br data-id="element-251" />
              <br data-id="element-252" />
              You can do more if you want, but the method was designed to be
              simple and easy to stick with — even on hard days.
            </p>} data-id="element-249" />
        <FAQItem question="What if I get too overwhelmed to do it?" answer={<p className="mb-4" data-id="element-254">
              That's okay. This program was built for those exact moments.
              <br data-id="element-255" />
              <br data-id="element-256" />
              You can lie down. You can pause. You can come back to it anytime.
              <br data-id="element-257" />
              <br data-id="element-258" />
              There's no wrong way to use the tools. Just press play and let
              your body guide you.
            </p>} data-id="element-253" />
        <FAQItem question="What happens after I buy?" answer={<>
              <p className="mb-4" data-id="element-260">
                As soon as you complete your order, you'll get instant access
                to:
              </p>
              <ul className="list-disc pl-6 mb-4" data-id="element-261">
                <li className="mb-2" data-id="element-262">All the daily reset videos</li>
                <li className="mb-2" data-id="element-263">Calming tools and audio sessions</li>
                <li className="mb-2" data-id="element-264">
                  A private member area to track your journey
                </li>
                <li className="mb-2" data-id="element-265">Our supportive online community</li>
              </ul>
              <p data-id="element-266">
                You'll also receive an email with your login details so you can
                come back anytime.
              </p>
            </>} data-id="element-259" />
        <FAQItem question="What if it doesn't work for me?" answer={<p className="mb-4" data-id="element-268">
              That's okay too.
              <br data-id="element-269" />
              <br data-id="element-270" />
              You're backed by a 30-day money-back guarantee. Try it. Use the
              tools. Feel it in your body.
              <br data-id="element-271" />
              <br data-id="element-272" />
              And if you don't notice a shift — or if it just doesn't feel right
              — send us a message and we'll refund you. No pressure. No stress.
            </p>} data-id="element-267" />
        <FAQItem question="Why should I join today?" answer={<p className="mb-4" data-id="element-274">
              Because your nervous system is already asking for help.
              <br data-id="element-275" />
              <br data-id="element-276" />
              And right now, we have a limited number of open spots so we can
              support each member well.
              <br data-id="element-277" />
              <br data-id="element-278" />
              We only open access in waves — and once it fills up, we close
              enrollment until the next cycle.
              <br data-id="element-279" />
              <br data-id="element-280" />
              So if you're feeling the pull… don't wait.
            </p>} data-id="element-273" />
      </div>
    </div>;
}